import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, DollarSign, TrendingUp, Wallet, Gauge } from 'lucide-react';
import TopBar from '../../components/TopBar.jsx';
import { apiGet } from '../../lib/api.js';

const RANGES = [
  { value: 'daily', label: 'Today' },
  { value: 'weekly', label: '7 days' },
  { value: 'monthly', label: '30 days' },
  { value: 'custom', label: 'Custom' },
];

const money = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** @param {number} x ROI multiple */
const roiClass = (x) => (x >= 3 ? 'text-emerald-300 bg-emerald-500/15' : x >= 1 ? 'text-amber-300 bg-amber-500/15' : 'text-rose-300 bg-rose-500/15');

function SummaryCard({ icon: Icon, label, value, tone = '' }) {
  return (
    <div className="panel p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-civix-purple/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-civix-purpleSoft" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-white/45">{label}</div>
        <div className={`text-2xl font-bold ${tone}`}>{value}</div>
      </div>
    </div>
  );
}

const COLUMNS = [
  { key: 'vendorName', label: 'Vendor / Lead Source', align: 'left' },
  { key: 'totalLeads', label: 'Leads Ingested' },
  { key: 'totalSpend', label: 'Total Cost', fmt: money },
  { key: 'totalSold', label: 'Policies Sold' },
  { key: 'conversionRate', label: 'Close Rate %', fmt: (n) => `${n}%` },
  { key: 'totalCommission', label: 'Gross Revenue', fmt: money },
  { key: 'netProfit', label: 'Net Profit', fmt: money },
  { key: 'roiMultiple', label: 'ROI Multiple' },
  { key: 'recommendationTag', label: 'Action Recommendation', align: 'left' },
];

/**
 * Vendor ROI leaderboard: spend, closed deals, revenue, net profit, ROI multiple.
 * @returns {JSX.Element}
 */
export default function LeadROI() {
  const [range, setRange] = useState('monthly');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState({ key: 'roiMultiple', dir: 'desc' });

  const params = range === 'custom' ? { range, from, to } : { range };
  const { data, isLoading, isError } = useQuery({
    queryKey: ['vendor-lead-roi', params],
    queryFn: () => apiGet('/analytics/lead-roi', params),
    enabled: range !== 'custom' || !!from,
  });

  const rows = useMemo(() => {
    const list = [...(data?.vendors || [])];
    const { key, dir } = sort;
    list.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : (av || 0) - (bv || 0);
      return dir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [data, sort]);

  const t = data?.totals || {};
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }));

  return (
    <div className="space-y-6">
      <TopBar title="Lead ROI" subtitle="What every lead source costs you vs. what it earns" />

      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map((r) => (
          <button
            key={r.value}
            type="button"
            onClick={() => setRange(r.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
              range === r.value
                ? 'bg-civix-purple/25 border-civix-purple text-white'
                : 'border-civix-border/50 text-white/60 hover:text-white'
            }`}
          >
            {r.label}
          </button>
        ))}
        {range === 'custom' && (
          <>
            <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
            <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
            <span className="text-xs text-white/40">max 90 days</span>
          </>
        )}
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <SummaryCard icon={Wallet} label="Total Lead Spend" value={money(t.totalSpend)} />
        <SummaryCard icon={DollarSign} label="Gross Commission Earned" value={money(t.totalCommission)} />
        <SummaryCard
          icon={TrendingUp}
          label="Net Profit"
          value={money(t.netProfit)}
          tone={t.netProfit < 0 ? 'text-rose-300' : 'text-emerald-300'}
        />
        <SummaryCard icon={Gauge} label="Overall Blended ROI" value={`${(t.roiMultiple || 0).toFixed(1)}x`} />
      </div>

      <div className="panel overflow-x-auto" data-testid="roi-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-white/40">
              {COLUMNS.map((c) => (
                <th key={c.key} className={`px-4 py-3 font-medium ${c.align === 'left' ? 'text-left' : 'text-right'}`}>
                  <button type="button" onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-white">
                    {c.label}
                    {sort.key === c.key && (sort.dir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-white/50">Loading…</td></tr>
            )}
            {isError && (
              <tr><td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-rose-300">Could not load ROI data.</td></tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-white/50">
                  No vendor leads in this period. Set a cost on a lead vendor to start tracking ROI.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={String(r.vendorId)} className="border-t border-civix-border/30">
                <td className="px-4 py-3 font-medium">{r.vendorName}</td>
                <td className="px-4 py-3 text-right">{r.totalLeads}</td>
                <td className="px-4 py-3 text-right">{money(r.totalSpend)}</td>
                <td className="px-4 py-3 text-right">{r.totalSold}</td>
                <td className="px-4 py-3 text-right">{r.conversionRate}%</td>
                <td className="px-4 py-3 text-right">{money(r.totalCommission)}</td>
                <td className={`px-4 py-3 text-right ${r.netProfit < 0 ? 'text-rose-300' : ''}`}>{money(r.netProfit)}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${roiClass(r.roiMultiple)}`}>
                    {r.roiMultiple.toFixed(1)}x
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${roiClass(r.roiMultiple)}`}>
                    {r.recommendationTag}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
